"use client";

import { c as _c } from "react/compiler-runtime";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import type { Mutable, NonNullableNotRequired } from "@/helpers/Types";
import { useContext, useEffect, useState } from "react";
import { commonApiFetch } from "@/services/api/common-api";
import GroupItems from "./GroupItems";
import { useSelector } from "react-redux";
import { selectActiveGroupId } from "@/store/groupSlice";
import GroupsSelectActiveGroup from "./GroupsSelectActiveGroup";
import type { ApiGroupFull } from "@/generated/models/ApiGroupFull";
import type { GroupsRequestParams } from "@/entities/IGroup";
import IdentitySearch, { IdentitySearchSize } from "@/components/utils/input/identity/IdentitySearch";
import { useDebounce } from "react-use";
import { AuthContext } from "@/components/auth/Auth";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
export default function GroupSelect() {
  const $ = _c(48);
  const activeGroupId = useSelector(selectActiveGroupId);
  const {
    connectedProfile,
    activeProfileProxy
  } = useContext(AuthContext);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      group_name: null,
      author_identity: null
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [filters, setFilters] = useState(t0);
  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  let t1;
  if ($[1] !== filters) {
    t1 = () => setDebouncedFilters(filters);
    $[1] = filters;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== filters) {
    t2 = [filters];
    $[3] = filters;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  useDebounce(t1, 200, t2);
  let t3;
  if ($[5] !== debouncedFilters) {
    t3 = {
      queryKey: [QueryKey.GROUPS_INFINITE, debouncedFilters],
      queryFn: async t4 => {
        const {
          pageParam
        } = t4;
        const params = {};
        if (debouncedFilters.group_name) {
          params.group_name = debouncedFilters.group_name;
        }
        if (debouncedFilters.author_identity) {
          params.author_identity = debouncedFilters.author_identity;
        }
        if (pageParam) {
          params.created_at_less_than = `${pageParam}`;
        }
        return await commonApiFetch({
          endpoint: "groups",
          params
        });
      },
      initialPageParam: null,
      getNextPageParam: _temp,
      placeholderData: keepPreviousData
    };
    $[5] = debouncedFilters;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    data
  } = useInfiniteQuery(t3);
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = value => {
      setFilters(prev => ({
        ...prev,
        author_identity: value
      }));
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const onUserSelect = t4;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = value_0 => {
      setFilters(prev_0 => ({
        ...prev_0,
        group_name: value_0
      }));
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const onFilterNameSearch = t5;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [];
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const [groups, setGroups] = useState(t6);
  let t7;
  if ($[10] !== data?.pages) {
    t7 = () => setGroups(data?.pages?.flat() ?? []);
    $[10] = data?.pages;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== activeGroupId || $[13] !== data) {
    t8 = [data, activeGroupId];
    $[12] = activeGroupId;
    $[13] = data;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  useEffect(t7, t8);
  let t9;
  if ($[15] !== activeProfileProxy?.created_by.handle || $[16] !== connectedProfile?.handle) {
    t9 = () => {
      const handle = activeProfileProxy?.created_by.handle ?? connectedProfile?.handle ?? null;
      if (!handle) {
        return;
      }
      onUserSelect(handle);
    };
    $[15] = activeProfileProxy?.created_by.handle;
    $[16] = connectedProfile?.handle;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const onShowMyGroups = t9;
  let t10;
  if ($[18] !== activeGroupId) {
    t10 = activeGroupId && <GroupsSelectActiveGroup activeGroupId={activeGroupId} />;
    $[18] = activeGroupId;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <p className="tw-text-base tw-text-iron-50 tw-font-semibold tw-mb-3">Search Groups</p>;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== connectedProfile?.handle || $[22] !== onShowMyGroups) {
    t12 = !!connectedProfile?.handle && <button onClick={onShowMyGroups} type="button" className="tw-text-sm tw-font-semibold tw-inline-flex tw-items-center tw-rounded-lg tw-bg-iron-800 tw-px-2 tw-py-1.5 tw-text-iron-300 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-inset tw-border-0 tw-ring-1 tw-ring-inset tw-ring-iron-700 hover:tw-bg-iron-700 focus:tw-z-10 tw-transition tw-duration-300 tw-ease-out">My groups</button>;
    $[21] = connectedProfile?.handle;
    $[22] = onShowMyGroups;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== filters.author_identity) {
    t13 = <IdentitySearch identity={filters.author_identity} setIdentity={onUserSelect} size={IdentitySearchSize.SM} label="By Identity" />;
    $[24] = filters.author_identity;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const t14 = filters.group_name ?? "";
  let t15;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = e => onFilterNameSearch(e.target.value);
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== t14) {
    t16 = <input type="text" id="floating-sidebar-group-name" value={t14} onChange={t15} autoComplete="off" className={"tw-py-3 tw-text-sm tw-form-input tw-block tw-pl-10 tw-pr-4 tw-w-full tw-rounded-lg tw-border-0 tw-appearance-none tw-text-white tw-border-iron-650 focus:tw-border-blue-500 tw-peer\n      tw-bg-iron-900 hover:tw-bg-iron-800 focus:tw-bg-iron-900 tw-font-medium tw-caret-primary-300 tw-shadow-sm tw-ring-1 tw-ring-inset tw-ring-iron-650 placeholder:tw-text-iron-500 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-inset focus:tw-ring-primary-400 tw-transition tw-duration-300 tw-ease-out"} placeholder=" " />;
    $[27] = t14;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== filters.group_name?.length) {
    t17 = !!filters.group_name?.length && <svg onClick={() => onFilterNameSearch(null)} className="tw-top-3.5 tw-cursor-pointer tw-absolute tw-right-3 tw-h-5 tw-w-5 tw-text-iron-300" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M17 7L7 17M7 7L17 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>;
    $[29] = filters.group_name?.length;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  let t19;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <svg className="tw-pointer-events-none tw-absolute tw-left-3 tw-top-3 tw-h-5 tw-w-5 tw-text-iron-300" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" /></svg>;
    t19 = <label htmlFor="floating-sidebar-group-name" className={"tw-absolute tw-cursor-text tw-text-sm tw-font-medium tw-text-iron-500 tw-duration-300 tw-transform -tw-translate-y-4 tw-scale-75 tw-top-2 tw-z-[1] tw-origin-[0] tw-bg-iron-900 tw-rounded-lg group-hover:tw-bg-iron-800 peer-focus:tw-bg-iron-900 tw-ml-7 tw-px-2 peer-focus:tw-px-2 peer-focus:tw-text-primary-400 peer-placeholder-shown:tw-scale-100 \n                        peer-placeholder-shown:-tw-translate-y-1/2 peer-placeholder-shown:tw-top-1/2 peer-focus:tw-top-2 peer-focus:tw-scale-75 peer-focus:-tw-translate-y-4 rtl:peer-focus:tw-translate-x-1/4 rtl:peer-focus:tw-left-auto tw-start-1"}>By Group Name</label>;
    $[31] = t18;
    $[32] = t19;
  } else {
    t18 = $[31];
    t19 = $[32];
  }
  let t20;
  if ($[33] !== t16 || $[34] !== t17) {
    t20 = <div className="tw-group tw-w-full tw-relative">{t16}{t17}{t18}{t19}</div>;
    $[33] = t16;
    $[34] = t17;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] !== t12 || $[37] !== t13 || $[38] !== t20) {
    t21 = <div className="tw-space-y-3">{t12}{t13}{t20}</div>;
    $[36] = t12;
    $[37] = t13;
    $[38] = t20;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== groups) {
    t22 = <div className="tw-divide-y tw-space-y-4 tw-divide-solid tw-divide-iron-800 tw-divide-x-0"><div className="tw-pt-4"><GroupItems groups={groups} /></div></div>;
    $[40] = groups;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== t21 || $[43] !== t22) {
    t23 = <div className="tw-px-4 tw-pt-3">{t11}{t21}{t22}</div>;
    $[42] = t21;
    $[43] = t22;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  let t24;
  if ($[45] !== t10 || $[46] !== t23) {
    t24 = <div className="tw-mt-4 tw-w-full tw-border-t tw-border-solid tw-border-iron-800 tw-border-x-0 tw-border-b-0 tw-divide-y tw-space-y-4 tw-divide-solid tw-divide-iron-800 tw-divide-x-0">{t10}{t23}</div>;
    $[45] = t10;
    $[46] = t23;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  return t24;
}
function _temp(lastPage) {
  return lastPage.at(-1)?.created_at ?? null;
}